import {exists} from '@aboutyou/utils';
import {isClientTimeoutExceededError, isNotFoundError} from './grpc/code';
import {logUseRequestError} from './logUseRequestError';
import {resolveStreamToPromise} from './stream/utils';

export const throwValidatedDecorator = <T extends object>({
  fetcherFunction,
  config,
  nonCriticalErrorMessagePatterns,
  cacheKey,
  setMetadataHandled,
  waitUntilMetadataHandled,
}: {
  fetcherFunction: () => Promise<T> | T;
  config?: {
    validateData?: (data: T) => void;
    validateError?: (error: unknown) => void;
    nonEssential?: boolean;
  };
  nonCriticalErrorMessagePatterns?: RegExp[];
  cacheKey: string;
  setMetadataHandled?: (handled: boolean) => void;
  waitUntilMetadataHandled?: () => Promise<true> | undefined;
}): (() => Promise<T>) => {
  return () => {
    const canModifyHeader = detectMightModifyHeader<T>(config);
    if (!process.browser && canModifyHeader) {
      setMetadataHandled?.(false);
    }

    const result = fetcherFunction();
    const promiseFn =
      result instanceof Promise ? result : Promise.resolve(result);

    return promiseFn.then(
      async mayBeData => {
        const data = process.browser
          ? mayBeData
          : await resolveStreamToPromise(mayBeData);

        try {
          config?.validateData?.(data as T);
        } catch (err) {
          throwValidationError(err);
        } finally {
          if (!process.browser && canModifyHeader) {
            setMetadataHandled?.(true);
          }
        }

        if (!process.browser && !canModifyHeader) {
          await waitUntilMetadataHandled?.();
        }

        return data as T;
      },
      error => {
        try {
          config?.validateError?.(error);
        } catch (error) {
          throwValidationError(error);
        } finally {
          if (!process.browser && canModifyHeader) {
            setMetadataHandled?.(true);
          }
        }

        let skipErrorLogging = false;
        let nonCriticalErrorMessage = false;

        if (config?.nonEssential && isClientTimeoutExceededError(error)) {
          skipErrorLogging = true;
        }

        if (nonCriticalErrorMessagePatterns?.length) {
          const message = error?.message;
          const stack = error?.stack;
          if (
            (message || stack) &&
            nonCriticalErrorMessagePatterns.some(
              pattern => pattern.test(message) || pattern.test(stack),
            )
          ) {
            skipErrorLogging = true;
            nonCriticalErrorMessage = true;
          }
        }

        // when there is a redirect or 404 error, all requests on the page starts throwing `NotFound` GRPC error code
        // this error is something we can ignore since the redirects and 404 are already handled separately by the MetaData endpoints, we dont want to additionally tag errors on those requests from other endpoints
        if (isNotFoundError(error)) {
          skipErrorLogging = true;
        }
        // No need to log validation errors, they are expected to be thrown and are handled at router level
        if (isValidationError(error)) {
          skipErrorLogging = true;
        }

        if (!skipErrorLogging) {
          logUseRequestError({cacheKey, error});
        }

        if (skipErrorLogging && nonCriticalErrorMessage) {
          logUseRequestError({cacheKey, error, asWarning: true});
        }

        throw error;
      },
    );
  };
};

const detectMightModifyHeader = <T>(config?: {
  validateData?: (data: T) => void;
  validateError?: (error: unknown) => void;
}) => {
  return config?.validateData || config?.validateError;
};

const VALIDATION_ERROR_ID = Symbol('VALIDATION_ERROR_ID');
type ValidationThrowableType = {
  __VALIDATION_ERROR_ID__: typeof VALIDATION_ERROR_ID;
  data: unknown;
};

export const isValidationError = (
  error: unknown | ValidationThrowableType,
): error is ValidationThrowableType => {
  return (
    exists(error) &&
    Object.prototype.hasOwnProperty.call(error, '__VALIDATION_ERROR_ID__') &&
    (error as ValidationThrowableType).__VALIDATION_ERROR_ID__ ===
      VALIDATION_ERROR_ID
  );
};

const throwValidationError = <T extends object>(data: T): never => {
  // eslint-disable-next-line no-throw-literal
  throw {
    __VALIDATION_ERROR_ID__: VALIDATION_ERROR_ID,
    data,
  };
};
